import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import {
  Sparkles,
  Trophy,
  Zap,
  Disc3,
  Music2,
  Headphones,
  Mic2,
  Star,
  Award,
  Medal,
  Gem,
  Crown,
  Rocket,
  Diamond,
  Lock,
  Check,
} from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import { getMyXp } from "@/lib/xp.functions";
import { cn } from "@/lib/utils";
import { getBadgeByName } from "@/lib/level-badges";

const LEVEL_ICONS = [
  Disc3,
  Music2,
  Headphones,
  Mic2,
  Star,
  Award,
  Medal,
  Trophy,
  Gem,
  Crown,
  Rocket,
  Diamond,
];

const LEVEL_TITLES = [
  "Iniciante",
  "Ouvinte",
  "Curioso",
  "Crítico",
  "Conhecedor",
  "Veterano",
  "Especialista",
  "Mestre",
  "Joia rara",
  "Lendário",
  "Visionário",
  "Diamante",
];

export const Route = createFileRoute("/levels")({
  head: () => ({ meta: [{ title: "Níveis e XP — Viny Club" }] }),
  component: LevelsPage,
});

function xpForLevel(level: number) {
  return level * level * 50;
}

const ACTIONS = [
  { icon: Zap, label: "Adicionar um disco à coleção", xp: 10 },
  { icon: Zap, label: "Escrever uma avaliação", xp: 15 },
  { icon: Zap, label: "Comentar em um álbum", xp: 5 },
  { icon: Zap, label: "Curtir um álbum", xp: 2 },
  { icon: Zap, label: "Seguir um colecionador", xp: 3 },
  { icon: Zap, label: "Manter sua sequência diária", xp: 5 },
];

function LevelsPage() {
  const fetchXp = useServerFn(getMyXp);
  const q = useQuery({
    queryKey: ["my-xp"],
    queryFn: () => fetchXp(),
    staleTime: 60_000,
  });

  const xp = q.data?.xp ?? 0;
  const level = q.data?.level ?? 1;
  const next = q.data?.nextLevelXp ?? 50;
  const prev = q.data?.prevLevelXp ?? 0;
  const pct = Math.min(100, Math.max(0, ((xp - prev) / Math.max(1, next - prev)) * 100));

  const ladder = Array.from({ length: 12 }).map((_, i) => {
    const lv = i + 1;
    return {
      level: lv,
      xpRequired: xpForLevel(lv),
      achieved: level >= lv,
      current: level === lv,
    };
  });

  return (
    <AppShell>
      <PageHeader eyebrow="Conta" title="Níveis e XP" subtitle="Sua evolução na comunidade, nível por nível." />
      <div className="mx-auto w-full max-w-[1400px] px-4 pb-24 lg:px-5">


        {/* Hero */}
        <section className="mt-4 overflow-hidden rounded-3xl border border-[color:var(--hairline)] bg-gradient-to-br from-primary/10 via-card to-card p-6 sm:p-8">
          <div className="inline-flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <h1 className="font-display text-2xl font-semibold tracking-tight">
              Níveis e XP
            </h1>
          </div>

          <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                Nível atual
              </div>
              <div className="font-display text-6xl font-semibold leading-none tabular-nums">
                {level}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                Experiência
              </div>
              <div className="font-display text-2xl font-semibold tabular-nums">
                {xp}{" "}
                <span className="text-sm font-normal text-muted-foreground">
                  / {next} XP
                </span>
              </div>
            </div>
          </div>

          <div className="mt-5 h-2.5 overflow-hidden rounded-full bg-surface">
            <div
              className="h-full rounded-full bg-gradient-to-r from-primary/60 to-primary"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Faltam <span className="font-semibold text-foreground">{Math.max(0, next - xp)} XP</span>{" "}
            para o nível {level + 1}.
          </p>
        </section>

        {/* Ladder */}
        <section className="mt-8 rounded-3xl border border-[color:var(--hairline)] bg-card p-6">
          <div className="inline-flex items-center gap-2">
            <Trophy className="h-4 w-4 text-amber-400" />
            <h2 className="font-display text-lg font-semibold tracking-tight">
              Escada de níveis
            </h2>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {ladder.map((l, i) => {
              const Icon = LEVEL_ICONS[i % LEVEL_ICONS.length];
              const title = LEVEL_TITLES[i % LEVEL_TITLES.length];
              const badge = getBadgeByName(title);
              const unlocked = l.achieved || l.current;
              return (
                <div
                  key={l.level}
                  style={{ animationDelay: `${i * 40}ms` }}
                  className={cn(
                    "group relative overflow-hidden rounded-2xl border p-4 animate-fade-in transition-all",
                    l.current
                      ? "border-primary/60 bg-[#1a1a1a] shadow-lg shadow-primary/10 ring-1 ring-primary/30"
                      : l.achieved
                        ? "border-[color:var(--hairline)] bg-[#1a1a1a] hover:border-primary/30"
                        : "border-[color:var(--hairline)] bg-surface/30",
                  )}
                >
                  <div className="flex items-start justify-between">
                    {badge ? (
                      <img
                        src={unlocked ? badge.unlocked : badge.locked}
                        alt={title}
                        loading="lazy"
                        className={cn(
                          "h-14 w-14 object-contain transition-transform group-hover:scale-105",
                          !unlocked && "opacity-70",
                        )}
                      />
                    ) : (
                      <div
                        className={cn(
                          "grid h-14 w-14 place-items-center rounded-full transition-transform group-hover:scale-105",
                          unlocked
                            ? "bg-primary/15 text-primary"
                            : "bg-muted/30 text-muted-foreground/50",
                        )}
                      >
                        {unlocked ? (
                          <Icon className="h-6 w-6" />
                        ) : (
                          <Lock className="h-4 w-4" />
                        )}
                      </div>
                    )}
                    {l.achieved && !l.current && (
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary/15 text-primary">
                        <Check className="h-3 w-3" strokeWidth={3} />
                      </span>
                    )}
                    {l.current && (
                      <span className="rounded-full bg-primary px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary-foreground">
                        Atual
                      </span>
                    )}
                  </div>
                  <div className="mt-3 flex items-baseline gap-1.5">
                    <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      Nv
                    </span>
                    <span
                      className={cn(
                        "font-display text-3xl font-semibold tabular-nums",
                        !l.achieved && !l.current && "text-muted-foreground/60",
                      )}
                    >
                      {l.level}
                    </span>
                  </div>
                  <div
                    className={cn(
                      "mt-0.5 text-sm font-medium",
                      l.current
                        ? "text-foreground"
                        : l.achieved
                          ? "text-foreground/90"
                          : "text-muted-foreground/70",
                    )}
                  >
                    {title}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {l.xpRequired} XP
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Actions */}
        <section className="mt-8 rounded-3xl border border-[color:var(--hairline)] bg-card p-6">
          <h2 className="font-display text-lg font-semibold tracking-tight">
            Como ganhar XP
          </h2>
          <ul className="mt-4 divide-y divide-[color:var(--hairline)]">
            {ACTIONS.map((a, i) => (
              <li key={i} className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <div className="grid h-8 w-8 place-items-center rounded-full bg-primary/10 text-primary">
                    <a.icon className="h-4 w-4" />
                  </div>
                  <span className="text-sm">{a.label}</span>
                </div>
                <span className="text-sm font-semibold tabular-nums text-primary">
                  +{a.xp} XP
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </AppShell>
  );
}
